import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ApprovalBlocker, PolicyChangeType } from '@deepguide-ai/types'
import { Button } from '@deepguide-ai/ui/button'
import { Checkbox } from '@deepguide-ai/ui/checkbox'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Textarea } from '@deepguide-ai/ui/textarea'
import { useParams } from '@tanstack/react-router'
import {
  AlertTriangle,
  Info,
  Loader2,
  MessageCircleQuestion,
  Sparkles,
} from 'lucide-react'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { useAskVeraAboutPolicyGap } from '@/components/screenata/policy-detail/useAskVeraAboutPolicyGap'

/**
 * Feature flag for the policy-approval workflow (#850 blocking gate + #1002
 * change-summary / classification). Mirrors the server constant in
 * policy-approval-readiness.service.ts. Off by default; flip a FlagOverride to
 * dark-launch per workspace.
 */
export const POLICY_APPROVAL_GATE_FLAG = 'policy_approval_gate'

export type PolicyApprovalOverrideInput = { reason: string }

/** #1002: args passed when approving — override (#850) + classification (#1002). */
export type ApprovePolicyArgs = {
  override?: PolicyApprovalOverrideInput
  changeType?: PolicyChangeType
}

interface ApprovePolicyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  programId: string
  policySlug: string
  policyTitle: string
  isApproving?: boolean
  onApprove: (args?: ApprovePolicyArgs) => Promise<void>
}

function blockerLabel(blocker: ApprovalBlocker) {
  switch (blocker.kind) {
    case 'contradicted_claim':
      return 'Contradicted claim'
    case 'gap_finding':
      return 'Open gap finding'
    default:
      return 'Hard commitment'
  }
}

export function ApprovePolicyDialog({
  open,
  onOpenChange,
  programId,
  policySlug,
  policyTitle,
  isApproving = false,
  onApprove,
}: ApprovePolicyDialogProps) {
  const queryClient = useQueryClient()
  const { workspaceId } = useParams({ strict: false })
  const askVeraAboutGap = useAskVeraAboutPolicyGap(
    workspaceId,
    policySlug,
    policyTitle
  )
  const [overrideChecked, setOverrideChecked] = useState(false)
  const [overrideReason, setOverrideReason] = useState('')
  const [refresh, setRefresh] = useState(false)
  // #1002: local edit buffer for the AI change summary (null = not editing).
  const [summaryDraft, setSummaryDraft] = useState<string | null>(null)
  // #1002: approver's classification choice (null = use Vera's suggestion).
  const [changeTypeChoice, setChangeTypeChoice] =
    useState<PolicyChangeType | null>(null)
  const readinessQuery = useQuery({
    ...trpc.complianceProgram.getPolicyApprovalReadiness.queryOptions({
      programId,
      policySlug,
      refresh,
    }),
    enabled: open && !!programId && !!policySlug,
  })
  const summaryMutation = useMutation(
    trpc.complianceProgram.updatePolicyChangeSummary.mutationOptions()
  )

  const readiness = readinessQuery.data
  const changeSummary = readiness?.changeSummary ?? null
  const blockers = useMemo(
    () => [
      ...(readiness?.hardCommitmentsUnresolved ?? []),
      ...(readiness?.claimsContradicted ?? []),
    ],
    [readiness]
  )
  // Tiered gate: unbacked commitments are actionable suggestions, never
  // blockers — they're frequently authored by our own templates.
  const commitmentSuggestions = readiness?.commitmentSuggestions ?? []
  const unverifiableClaims = readiness?.claimsUnverifiable ?? []
  const unlinkedCommitments = readiness?.claimsUnlinked ?? []
  const blockingUnlinkedCommitments = unlinkedCommitments.filter(
    (commitment) => commitment.blocking
  )
  const warningUnlinkedCommitments = unlinkedCommitments.filter(
    (commitment) => !commitment.blocking
  )
  const reason = overrideReason.trim()
  const hasReadiness = !!readiness
  const blocked = !!readiness?.isBlocked
  // Classification only applies when there's a change to classify (re-approval).
  // No silent default: if Vera couldn't suggest and the approver hasn't chosen,
  // leave it unset rather than quietly recording 'non_material'.
  const effectiveChangeType: PolicyChangeType | null = changeSummary
    ? (changeTypeChoice ?? changeSummary.suggestedChangeType ?? null)
    : null
  const canApprove =
    hasReadiness && (!blocked || (overrideChecked && reason.length >= 5))

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen)
    if (!nextOpen) {
      setOverrideChecked(false)
      setOverrideReason('')
      setRefresh(false)
      setSummaryDraft(null)
      setChangeTypeChoice(null)
    }
  }

  const handleSaveSummary = async () => {
    if (summaryDraft === null) return
    try {
      await summaryMutation.mutateAsync({
        programId,
        policySlug,
        changeSummary: summaryDraft,
      })
      await queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyApprovalReadiness.queryKey({
          programId,
          policySlug,
        }),
      })
      setSummaryDraft(null)
      toast.success('Change summary updated')
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to save change summary'
      )
    }
  }

  const handleRefresh = async () => {
    try {
      setRefresh(true)
      await queryClient.fetchQuery(
        trpc.complianceProgram.getPolicyApprovalReadiness.queryOptions({
          programId,
          policySlug,
          refresh: true,
        })
      )
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to run AI approval review'
      )
    }
  }

  const handleApprove = async () => {
    try {
      await onApprove({
        override: blocked ? { reason } : undefined,
        changeType: effectiveChangeType ?? undefined,
      })
      handleOpenChange(false)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to approve policy'
      )
    }
  }

  /**
   * Opens Vera in the side panel with the gap as context. The approval
   * dialog closes first — it's a modal, so its overlay would sit on top of
   * the chat panel and swallow every click.
   */
  const handleAskVera = (gap: Parameters<typeof askVeraAboutGap>[0]) => {
    handleOpenChange(false)
    askVeraAboutGap(gap)
  }


  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='!max-w-2xl gap-0 p-0'>
        <DialogHeader className=' px-6 py-4 pr-12'>
          <DialogTitle>Approve {policyTitle}</DialogTitle>
          <DialogDescription>
            Review approval readiness before signing this policy version.
          </DialogDescription>
        </DialogHeader>
        {/* `min-w-0` is load-bearing: DialogContent is a grid, so an item whose
            min-content width exceeds the track widens the whole track — the
            body AND the footer then paint outside the dialog's background.
            `[&>div]:!block` retargets Radix's inner `display:table` wrapper,
            which otherwise sizes to the longest `truncate` (nowrap) claim
            rather than to the dialog. */}
        <ScrollArea
          className='min-w-0'
          viewClassName='max-h-[70vh] [&>div]:!block'
        >
          <div className='space-y-4 px-6 py-4'>
            {changeSummary ? (
              <div className='border-border bg-status-blue-bg rounded-sm border p-3'>
                <div className='flex items-center justify-between gap-2'>
                  <div className='text-status-blue flex items-center gap-1.5 text-[11px] font-medium tracking-[0.08em] uppercase'>
                    <Sparkles className='h-3.5 w-3.5' />
                    What changed since last approved
                  </div>
                  {summaryDraft === null ? (
                    <button
                      type='button'
                      className='text-status-blue text-[11px] font-medium hover:underline'
                      onClick={() => setSummaryDraft(changeSummary.text)}
                    >
                      Edit
                    </button>
                  ) : null}
                </div>
                {summaryDraft === null ? (
                  <div className='text-text-body mt-2 text-sm leading-6 whitespace-pre-wrap'>
                    {changeSummary.text}
                  </div>
                ) : (
                  <div className='mt-2 space-y-2'>
                    <Textarea
                      value={summaryDraft}
                      onChange={(event) => setSummaryDraft(event.target.value)}
                      rows={4}
                    />
                    <div className='flex justify-end gap-2'>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        className='h-8 text-xs'
                        disabled={summaryMutation.isPending}
                        onClick={() => setSummaryDraft(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type='button'
                        size='sm'
                        className='h-8 text-xs'
                        disabled={summaryMutation.isPending}
                        onClick={() => void handleSaveSummary()}
                      >
                        {summaryMutation.isPending ? (
                          <Loader2 className='h-3.5 w-3.5 animate-spin' />
                        ) : null}
                        Save
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
            {changeSummary ? (
              <div className='border-border rounded-sm border bg-card p-3'>
                <div className='text-text-subtle text-[11px] font-medium tracking-[0.08em] uppercase'>
                  Change classification
                </div>
                <div className='mt-2 grid grid-cols-2 gap-2'>
                  {[
                    {
                      value: 'material' as const,
                      label: 'Material',
                      hint: 'Employees should re-acknowledge',
                    },
                    {
                      value: 'non_material' as const,
                      label: 'Non-material',
                      hint: 'Re-acknowledgment not needed',
                    },
                  ].map((option) => {
                    const selected = effectiveChangeType === option.value
                    return (
                      <button
                        key={option.value}
                        type='button'
                        onClick={() => setChangeTypeChoice(option.value)}
                        className={`rounded-sm border p-2 text-left transition-colors ${
                          selected
                            ? 'border-ring bg-[#F6F8FE] dark:bg-status-blue-bg'
                            : 'border-border bg-card hover:bg-gray-50 dark:hover:bg-surface-subtle'
                        }`}
                      >
                        <div className='text-text-secondary text-sm font-medium'>
                          {option.label}
                        </div>
                        <div className='text-text-subtle text-[11px]'>
                          {option.hint}
                        </div>
                      </button>
                    )
                  })}
                </div>
                {effectiveChangeType === changeSummary.suggestedChangeType &&
                changeSummary.suggestedChangeType ? (
                  <div className='text-text-subtle mt-2 text-[11px]'>
                    Suggested by Vera. Change it if you disagree.
                  </div>
                ) : null}
              </div>
            ) : null}
            {readinessQuery.isLoading ? (
              <div className='text-muted-foreground flex items-center gap-2 text-sm'>
                <Loader2 className='h-4 w-4 animate-spin' />
                Checking approval readiness
              </div>
            ) : readinessQuery.isError ? (
              <div className='rounded-sm border border-[#F1C9C9] bg-[#FFF8F8] p-3 text-sm text-status-red-ink dark:border-status-red/30 dark:bg-status-red-bg dark:text-status-red-strong'>
                Unable to check approval readiness. Run AI review and try again.
              </div>
            ) : null}

            {!readinessQuery.isLoading &&
            !readinessQuery.isError &&
            unverifiableClaims.length > 0 ? (
              <div className='rounded-sm border border-[#E1E8F7] bg-[#F6F8FE] p-3 dark:border-status-blue/30 dark:bg-status-blue-bg'>
                <div className='flex items-start gap-2'>
                  <Info className='mt-0.5 h-4 w-4 text-status-blue' />
                  <div className='min-w-0'>
                    <div className='text-sm font-semibold text-[#243E88] dark:text-status-blue-strong'>
                      Some claims are not yet verifiable
                    </div>
                    <div className='mt-1 text-xs leading-5 text-[#4A5D90] dark:text-status-blue'>
                      {unverifiableClaims.length === 1
                        ? '1 claim has no passing or failing API run yet.'
                        : `${unverifiableClaims.length} claims have no passing or failing API run yet.`}
                    </div>
                    <div className='mt-2 max-h-[120px] space-y-1 overflow-y-auto pr-1'>
                      {unverifiableClaims.slice(0, 5).map((claim, index) => (
                        <div
                          key={`${claim.claimId ?? claim.draftClaimId ?? index}`}
                          className='truncate text-xs text-[#333333] dark:text-text-body'
                          title={claim.claimText}
                        >
                          {claim.claimText}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Suggested improvements — visible whether or not approval is
                blocked. Each row carries its fix action so the user is never
                told about a gap without a way out. */}
            {!readinessQuery.isLoading &&
            !readinessQuery.isError &&
            (commitmentSuggestions.length > 0 ||
              blockingUnlinkedCommitments.length > 0) ? (
              <div className='space-y-2'>
                <div className='border-status-blue/30 bg-status-blue-bg rounded-sm border p-3'>
                  <div className='flex items-start gap-2'>
                    <Info className='text-status-blue mt-0.5 h-4 w-4' />
                    <div>
                      <div className='text-status-blue text-sm font-semibold'>
                        Implementation and evidence follow-ups
                      </div>
                      <div className='text-text-body mt-1 text-xs leading-5'>
                        Approval adopts the policy requirements. Linked tests and
                        remediation continue to track implementation and evidence.
                        Approving does not mark tests as passed.
                      </div>
                    </div>
                  </div>
                </div>
                {commitmentSuggestions.map((suggestion, index) => (
                  <div
                    key={`${suggestion.title}-${index}`}
                    className='border-border rounded-sm border bg-card p-3'
                  >
                    <div className='flex items-start justify-between gap-3'>
                      <div className='min-w-0'>
                        <div className='text-text-subtle text-[11px] font-medium tracking-[0.08em] uppercase'>
                          {blockerLabel(suggestion)}
                          {suggestion.severity ? ` · ${suggestion.severity}` : ''}
                        </div>
                        <div className='text-text-secondary mt-1 text-sm font-medium'>
                          {suggestion.title}
                        </div>
                        {suggestion.detail ? (
                          <div className='mt-1 text-xs leading-5 text-[#5A5A5A] dark:text-text-body'>
                            {suggestion.detail}
                          </div>
                        ) : null}
                      </div>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        className='h-8 shrink-0 text-xs'
                        onClick={() =>
                          handleAskVera({
                            label: blockerLabel(suggestion),
                            title: suggestion.title,
                            detail: suggestion.detail,
                            severity: suggestion.severity,
                          })
                        }
                      >
                        <MessageCircleQuestion className='h-3.5 w-3.5' />
                        How do I fix this?
                      </Button>
                    </div>
                  </div>
                ))}
                {blockingUnlinkedCommitments.map((commitment) => (
                  <div
                    key={commitment.occurrenceId ?? commitment.claimId}
                    className='border-border rounded-sm border bg-card p-3'
                  >
                    <div className='flex items-start justify-between gap-3'>
                      <div className='min-w-0'>
                        <div className='text-text-subtle text-[11px] font-medium tracking-[0.08em] uppercase'>
                          Unlinked commitment
                        </div>
                        <div className='text-text-secondary mt-1 text-sm font-medium'>
                          {commitment.claimText}
                        </div>
                        <div className='text-text-subtle mt-1 text-xs leading-5'>
                          This assertion has no linked test yet.
                        </div>
                      </div>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        className='h-8 shrink-0 text-xs'
                        onClick={() =>
                          handleAskVera({
                            label: 'Unlinked commitment',
                            title: 'This assertion has no linked test',
                            detail: commitment.claimText,
                          })
                        }
                      >
                        <MessageCircleQuestion className='h-3.5 w-3.5' />
                        How do I fix this?
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {!readinessQuery.isLoading &&
            !readinessQuery.isError &&
            warningUnlinkedCommitments.length > 0 ? (
              <div className='border-status-orange/30 bg-status-orange-bg rounded-sm border p-3'>
                <div className='flex items-start gap-2'>
                  <Info className='text-status-orange mt-0.5 h-4 w-4' />
                  <div className='min-w-0'>
                    <div className='text-status-orange text-sm font-semibold'>
                      Unlinked commitments (warning only)
                    </div>
                    <div className='text-status-orange mt-1 text-xs leading-5'>
                      Gaps, accepted risks, compensating controls, and diverged
                      passages do not block approval, but remain unresolved.
                    </div>
                    <div className='mt-2 max-h-[120px] space-y-1 overflow-y-auto pr-1'>
                      {warningUnlinkedCommitments
                        .slice(0, 5)
                        .map((commitment) => (
                          <div
                            key={commitment.occurrenceId ?? commitment.claimId}
                            className='text-text-body truncate text-xs'
                            title={commitment.claimText}
                          >
                            {commitment.claimText}
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {!readinessQuery.isLoading && !readinessQuery.isError && blocked ? (
              <>
                <div className='rounded-sm border border-[#F1C9C9] bg-[#FFF8F8] p-3 dark:border-status-red/30 dark:bg-status-red-bg'>
                  <div className='flex items-start gap-2'>
                    <AlertTriangle className='text-status-red mt-0.5 h-4 w-4' />
                    <div>
                      <div className='text-sm font-semibold text-status-red-ink dark:text-status-red-strong'>
                        Approval blocked by unresolved gaps
                      </div>
                      <div className='mt-1 text-xs leading-5 text-[#8A4B42] dark:text-status-red'>
                        Proceeding requires an explicit override reason that will
                        be stored with the approval signature.
                      </div>
                    </div>
                  </div>
                </div>

                <div className='max-h-[260px] space-y-2 overflow-y-auto pr-1'>
                  {blockers.map((blocker, index) => (
                    <div
                      key={`${blocker.findingKey ?? blocker.title}-${index}`}
                      className='border-border rounded-sm border bg-card p-3'
                    >
                      <div className='flex items-start justify-between gap-3'>
                        <div className='min-w-0'>
                          <div className='text-text-subtle text-[11px] font-medium tracking-[0.08em] uppercase'>
                            {blockerLabel(blocker)}
                            {blocker.severity ? ` · ${blocker.severity}` : ''}
                          </div>
                          <div className='text-text-secondary mt-1 text-sm font-medium'>
                            {blocker.title}
                          </div>
                          {blocker.detail ? (
                            <div className='mt-1 text-xs leading-5 text-[#5A5A5A] dark:text-text-body'>
                              {blocker.detail}
                            </div>
                          ) : null}
                        </div>
                        <div className='flex shrink-0 items-center gap-2'>
                          {/* Available on every blocker. Hard commitments carry
                              no findingKey, so this is their only route to help. */}
                          <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            className='h-8 text-xs'
                            onClick={() =>
                              handleAskVera({
                                label: blockerLabel(blocker),
                                title: blocker.title,
                                detail: blocker.detail,
                                severity: blocker.severity,
                              })
                            }
                          >
                            <MessageCircleQuestion className='h-3.5 w-3.5' />
                            How do I fix this?
                          </Button>
                          {/* "Assign to Vera" removed: `assignFindingToVera`
                              enqueues an AgentTask with `domain: 'policy_gap'`,
                              and nothing consumes that domain — the string
                              appears only in the producer. The task sat QUEUED
                              forever while the toast claimed Vera was on it.
                              Restore this button once a worker handles the
                              domain; the server procedure is left in place.
                              "How do I fix this?" above is the working route. */}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <label className='border-border bg-surface-subtle flex items-center gap-3 rounded-sm border p-3'>
                  <Checkbox
                    checked={overrideChecked}
                    onCheckedChange={(checked) =>
                      setOverrideChecked(Boolean(checked))
                    }
                    className='mt-0.5'
                  />
                  <span className='text-text-body text-sm leading-5'>
                    Approve with known gaps
                  </span>
                </label>

                <Textarea
                  value={overrideReason}
                  onChange={(event) => setOverrideReason(event.target.value)}
                  placeholder='Reason for approving with known gaps'
                  rows={3}
                  disabled={!overrideChecked}
                />
              </>
            ) : !readinessQuery.isLoading &&
              !readinessQuery.isError &&
              commitmentSuggestions.length === 0 &&
              blockingUnlinkedCommitments.length === 0 ? (
              <div className='rounded-sm border border-[#DDEAD8] bg-[#F7FBF5] p-3 text-sm text-[#2F5E2E] dark:border-status-green/30 dark:bg-status-green-bg dark:text-status-green-strong'>
                No blocking approval gaps were found for this policy version.
              </div>
            ) : null}
          </div>
        </ScrollArea>

        <DialogFooter className=' px-6 py-4'>
          <Button
            type='button'
            variant='outline'
            onClick={() => void handleRefresh()}
            disabled={readinessQuery.isFetching || isApproving}
          >
            {readinessQuery.isFetching ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <Sparkles className='h-4 w-4' />
            )}
            {readinessQuery.isFetching ? 'Running AI review' : 'Run AI review'}
          </Button>
          <Button
            type='button'
            onClick={handleApprove}
            disabled={
              readinessQuery.isLoading ||
              readinessQuery.isFetching ||
              isApproving ||
              !canApprove
            }
          >
            {isApproving ? <Loader2 className='h-4 w-4 animate-spin' /> : null}
            {blocked ? 'Approve with known gaps' : 'Approve'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
